import type {
  GambleSlotRequest,
  GambleSlotResponse,
  SlotTrailState,
  SpinSlotRequest,
  SpinSlotResponse,
  TrailGameResponse,
  TrailPickResponse,
} from '@goodtime/shared';
import { api, ApiError } from './client';

/**
 * {@code POST /v1/sessions/{id}/spin} — server-authoritative slot spin.
 * Body carries the bet; server pessimistically locks the wallet,
 * validates affordability, generates outcome, settles, credits net
 * winnings — all atomic. Response carries outcome (symbol ids per
 * cell), settlement, post-spin wallet, and the audit row id.
 *
 * <p>Throws {@link ApiError} on any non-2xx response — the slot scene
 * surfaces "spin failed" to the player and rolls back to idle.
 */
export function spinSlot(sessionId: string, req: SpinSlotRequest): Promise<SpinSlotResponse> {
  return api<SpinSlotResponse>(`/sessions/${encodeURIComponent(sessionId)}/spin`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** {@code POST /v1/sessions/{id}/gamble}: double-or-nothing on the session's
 *  latest win. The server picks the colour and moves the wallet; a retried
 *  step (same key) gets the first answer. Throws {@link ApiError} on a
 *  refusal (409) or any other failure; the slot then keeps the win as it was. */
export function gambleSlot(sessionId: string, req: GambleSlotRequest): Promise<GambleSlotResponse> {
  return api<GambleSlotResponse>(`/sessions/${encodeURIComponent(sessionId)}/gamble`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `${req.spinId}-g${req.step}` },
    body: JSON.stringify(req),
  });
}

/** {@code GET /v1/sessions/{id}/trail?themeId=}: the Sweet Trail for this
 *  slot, or null when the slot has none (empty body) or on any error. */
export async function fetchTrail(sessionId: string, themeId: string): Promise<SlotTrailState | null> {
  try {
    const t = await api<SlotTrailState | null>(
      `/sessions/${encodeURIComponent(sessionId)}/trail?themeId=${encodeURIComponent(themeId)}`,
    );
    return t ?? null;
  } catch {
    return null;
  }
}

/** {@code POST /v1/sessions/{id}/trail/pick}: open one bonus box. */
export function pickTrailBox(sessionId: string, themeId: string, index: number): Promise<TrailPickResponse> {
  return api<TrailPickResponse>(`/sessions/${encodeURIComponent(sessionId)}/trail/pick`, {
    method: 'POST',
    body: JSON.stringify({ themeId, index }),
  });
}

/** {@code POST /v1/sessions/{id}/trail/game}: open the mini-game the trail's
 *  bonus rolled (trail_games), paying its gems. The key names the bonus (its
 *  slot and level) and the session, so a retry is answered with the same
 *  play and gems, paid once. A refusal is a 409 with a code
 *  ({@link trailGameRefusal}). */
export function openTrailGame(sessionId: string, themeId: string, level: number): Promise<TrailGameResponse> {
  return api<TrailGameResponse>(`/sessions/${encodeURIComponent(sessionId)}/trail/game`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `trail-game-${themeId}-${level}-${sessionId.slice(0, 8)}` },
    body: JSON.stringify({ themeId }),
  });
}

/** Why the server wouldn't open the trail's game (409 `{error}`:
 *  `TRAIL_NO_BONUS`, `TRAIL_NO_GAME`, `TRAIL_BOXES_STARTED`,
 *  `TRAIL_GAMES_OFF`, `TRAIL_KEY_USED`), or null for any other failure. */
export function trailGameRefusal(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const code = (JSON.parse(err.body) as { error?: unknown }).error;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
}
